import { useEffect, useState } from 'react';
import { Navigate, Outlet, Route, Routes, useNavigate, useParams } from 'react-router';
import {
  eventName,
  eventPlace,
  sortEvents,
  type EventRecap,
  type RecapCount,
  type WrapUpSummary,
} from '@wr/shared';
import { ApiError, errorMessage, get, post } from '../../api.js';
import { useCurrentEvent } from '../../components/CurrentEvent.js';
import { Icon } from '../../components/Icon.js';
import { useToast } from '../../components/Toast.js';
import { Dialog, EmptyState, Notice } from '../../components/ui.js';
import { useOffline } from '../../offline/context.js';
import type { LocalRecord } from '../../offline/store.js';
import { useSignedIn } from '../../session.js';
import { useStages, stageName } from '../leads/stages.js';
import { shortDate } from '../leads/LeadSheet.js';

/**
 * Event recap (brief section 7.2, docs/prototype.html), mounted at
 * /recap/*: the current event's by default, any event from the list.
 * Live during the event and for 30 days after its wrap-up, then the
 * summary frozen at the wrap-up. No follow-up or task list.
 */
export function RecapScreen() {
  return (
    <Routes>
      <Route index element={<RecapIndex />} />
      <Route path=":id" element={<RecapPage />}>
        <Route index element={null} />
        <Route path="wrap-up" element={<WrapUpWindow back="recap" />} />
      </Route>
    </Routes>
  );
}

/** Opens the current event's recap, else the latest event's. */
function RecapIndex() {
  const { eventId, events } = useCurrentEvent();
  if (eventId === undefined) return <div className="spinner" aria-label="Loading" />;
  const target =
    eventId ?? sortEvents(events).find((e) => e.status === 'completed')?.id ?? events[0]?.id;
  if (!target) {
    return (
      <>
        <div className="page-head">
          <h1 className="page">Event recap</h1>
        </div>
        <EmptyState title="No event yet." />
      </>
    );
  }
  return <Navigate to={target} replace />;
}

/** Wrap up from the side menu: the current event's. */
export function WrapUpCurrent() {
  const { eventId } = useCurrentEvent();
  if (eventId === undefined) return <div className="spinner" aria-label="Loading" />;
  if (!eventId) {
    return (
      <>
        <div className="page-head">
          <h1 className="page">Wrap up</h1>
        </div>
        <EmptyState title="No current event.">
          Wrap up closes the current event. Pick it first, or use Wrap up on its card in Events.
        </EmptyState>
      </>
    );
  }
  return <Navigate to={`/recap/${eventId}/wrap-up`} replace />;
}

function RecapPage() {
  const { id = '' } = useParams();
  const { user, can, refresh } = useSignedIn();
  const { counts } = useOffline();
  const { events } = useCurrentEvent();
  const stages = useStages();
  const navigate = useNavigate();
  const [recap, setRecap] = useState<EventRecap | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let current = true;
    get<EventRecap>(`/events/${encodeURIComponent(id)}/recap`)
      .then((r) => {
        if (!current) return;
        setError(null);
        setRecap(r);
      })
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) {
          setError(
            err instanceof ApiError && err.status === 404
              ? 'This event does not exist, or it was deleted.'
              : errorMessage(err),
          );
        }
      });
    return () => {
      current = false;
    };
    // counts: a sync ran (a wrap-up done elsewhere, new captures).
  }, [id, refresh, counts]);

  const event = events.find((e) => e.id === id);
  const name = event ? eventName(event) : 'Event';
  const choices = sortEvents(events);
  const closed = recap?.wrappedUpAt ?? null;
  const head = (
    <>
      {event && (
        <p className="hello-date">
          {[
            closed ? `Completed ${shortDate(closed)}` : 'Live, the event is still open',
            eventPlace(event),
          ]
            .filter(Boolean)
            .join(', ')}
        </p>
      )}
      <div className="page-head">
        <h1 className="page">{name} recap</h1>
        {choices.length > 1 && (
          <select
            className="rc-event"
            aria-label="Event"
            value={id}
            onChange={(e) => navigate(`/recap/${e.target.value}`)}
          >
            {choices.map((e) => (
              <option key={e.id} value={e.id}>
                {eventName(e)}
              </option>
            ))}
          </select>
        )}
      </div>
    </>
  );

  if (!recap) {
    return (
      <>
        {head}
        {error ? (
          <Notice kind="error">{error}</Notice>
        ) : (
          <div className="spinner" aria-label="Loading" />
        )}
        <Outlet />
      </>
    );
  }

  const r = recap;
  const people = Math.max(1, r.contacts, r.qualified, r.disqualified, r.leadsToDocument);
  return (
    <>
      {head}
      {r.frozen && (
        <Notice>
          The records of this event left the portal 30 days after its wrap-up; this is the summary
          saved then. HubSpot keeps everything.
        </Notice>
      )}
      <div className="kpis recap-kpis">
        <Kpi value={r.contacts} label="Contacts" />
        <Kpi value={r.qualified} label="Qualified leads" />
        <Kpi value={r.leads} label="Leads" />
        <Kpi value={r.spendTotal ?? 'None'} label="Spend" />
      </div>
      <div className="recap-grid">
        <div className="tcard">
          <h3>Contacts and leads</h3>
          <div className="rc-body">
            <Bars
              max={people}
              items={[
                { name: 'Contacts', count: r.contacts },
                { name: 'Leads, qualified', count: r.qualified },
                { name: 'Leads, disqualified', count: r.disqualified },
                { name: 'To document', count: r.leadsToDocument },
              ]}
            />
            {r.contacts > 0 && (
              <p className="note">
                {r.withEmail} with email, {r.withLinkedIn} with LinkedIn, {r.toVerify} to verify.
              </p>
            )}
          </div>
        </div>
        <div className="tcard">
          <h3>Leads by stage</h3>
          <div className="rc-body">
            {r.stages.length ? (
              <Bars
                max={r.leads}
                items={r.stages.map((s) => ({ name: stageName(s.name, stages), count: s.count }))}
              />
            ) : (
              <p className="note">{r.leads ? 'No stage set yet.' : 'No leads yet.'}</p>
            )}
          </div>
          <h3>HubSpot</h3>
          <div className="rc-body">
            <Bars
              grey
              max={Math.max(1, r.inHubSpot, r.notDocumented)}
              items={[
                { name: 'In HubSpot', count: r.inHubSpot },
                { name: 'Not documented yet', count: r.notDocumented },
              ]}
            />
          </div>
        </div>
      </div>
      {/* Prototype: Markets, Team and Spend take the full width, one under the other. */}
      <div className="recap-wide">
        <div className="tcard">
          <h3>Markets</h3>
          <div className="rc-body">
            {r.markets.length ? (
              <Bars max={r.leads} items={r.markets} />
            ) : (
              <p className="note">No leads yet.</p>
            )}
          </div>
          <h3>Countries</h3>
          <div className="rc-body">
            {r.countries.length ? (
              <Bars grey max={r.countries[0]!.count} items={r.countries} />
            ) : (
              <p className="note">No data.</p>
            )}
          </div>
        </div>
        <div className="tcard">
          <h3>Team</h3>
          {r.team.length ? (
            r.team.map((t) => (
              <div key={t.userId} className="task">
                <span className="tbody">
                  <span className="tt">
                    {t.name}
                    {t.userId === user.id ? ' (you)' : ''}
                  </span>
                  <span className="ts">
                    {t.contacts} contacts, {t.leads} leads
                  </span>
                </span>
              </div>
            ))
          ) : (
            <p className="tfoot">Nobody yet.</p>
          )}
        </div>
        <div className="tcard">
          <h3>Spend</h3>
          {r.spend.length ? (
            <>
              {r.spend.map((s) => (
                <div key={s.budgetLine} className="task">
                  <span className="tbody">
                    <span className="tt">{s.budgetLine}</span>
                    <span className="ts">
                      {s.receipts} {s.receipts === 1 ? 'receipt' : 'receipts'}
                    </span>
                  </span>
                  <b className="xamt">{s.total}</b>
                </div>
              ))}
              <p className="tfoot">Total {r.spendTotal}.</p>
            </>
          ) : (
            <p className="tfoot">No expenses.</p>
          )}
        </div>
      </div>
      {!closed && can('events.wrapup') && event && event.status !== 'completed' && (
        <div className="actions recap-actions">
          <button type="button" className="btn primary" onClick={() => navigate('wrap-up')}>
            Wrap up {name}
          </button>
        </div>
      )}
      <Outlet />
    </>
  );
}

function Kpi({ value, label }: { value: number | string; label: string }) {
  return (
    <div className="kpi">
      <b>{value}</b>
      <span>{label}</span>
    </div>
  );
}

/** Horizontal bars (prototype: .hbar), each to the scale of max. */
function Bars({ items, max, grey }: { items: RecapCount[]; max: number; grey?: boolean }) {
  return (
    <>
      {items.map((i) => (
        <div key={i.name} className="hbar">
          <span>{i.name}</span>
          <span className="htrack">
            <i
              className={grey ? 'grey' : undefined}
              style={{ width: `${Math.max(3, (i.count / Math.max(1, max)) * 100)}%` }}
            />
          </span>
          <b>{i.count}</b>
        </div>
      ))}
    </>
  );
}

/**
 * Wrap up (brief section 7.1, admins): closes the event (Completed, no
 * more capture), lists what is not documented yet (it stays in To do for
 * its owner) and freezes the recap summary. It pushes nothing: the push
 * is automatic. Needs the network.
 */
export function WrapUpWindow({ back }: { back: 'recap' | 'events' }) {
  const { id = '' } = useParams();
  const { user, can, refresh } = useSignedIn();
  const { eventId, setEventId, sendNow } = useOffline();
  const { events } = useCurrentEvent();
  const navigate = useNavigate();
  const toast = useToast();
  const [summary, setSummary] = useState<WrapUpSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const event = events.find((e) => e.id === id) as LocalRecord | undefined;
  const name = event ? eventName(event) : 'this event';
  const close = () => navigate(back === 'recap' ? `/recap/${id}` : '/events', { replace: true });

  useEffect(() => {
    let current = true;
    get<WrapUpSummary>(`/events/${encodeURIComponent(id)}/wrap-up`)
      .then((s) => current && setSummary(s))
      .catch((err: unknown) => {
        if (err instanceof ApiError && err.status === 401) void refresh();
        else if (current) setError(errorMessage(err));
      });
    return () => {
      current = false;
    };
  }, [id, refresh]);

  if (!can('events.wrapup')) {
    return (
      <Dialog small title="Not allowed" onClose={close}>
        <p className="note">Only an administrator wraps up an event.</p>
      </Dialog>
    );
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      await post(`/events/${encodeURIComponent(id)}/wrap-up`, {});
      // The event is now Completed: it is no longer this device's current event.
      if (eventId === id) await setEventId(null);
      else await sendNow();
      toast(`${name} wrapped up.`);
      navigate(`/recap/${id}`, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  const todo = summary?.notDocumented ?? [];
  return (
    <Dialog
      title={`Wrap up ${name}`}
      onClose={close}
      footer={
        <>
          <button type="button" className="btn" onClick={close}>
            Not yet
          </button>
          <button
            type="button"
            className="btn primary"
            disabled={!summary || busy}
            onClick={() => void confirm()}
          >
            Close {name}
          </button>
        </>
      }
    >
      <p className="wrap-intro">
        Closes the event: it moves to Completed, nobody can capture on it anymore, and its recap
        summary is saved.
      </p>
      {!summary && !error && <div className="spinner" aria-label="Loading" />}
      {summary && (
        <>
          <div className="wrap-stats">
            <div>
              <b>{summary.contacts}</b>
              <span>Contacts</span>
            </div>
            <div>
              <b>{summary.leads}</b>
              <span>Leads</span>
            </div>
            <div>
              <b>{summary.inHubSpot}</b>
              <span>Records in HubSpot</span>
            </div>
          </div>
          {todo.length > 0 ? (
            <>
              <div className="dup">
                <b>
                  {todo.length} {todo.length === 1 ? 'record' : 'records'} not documented yet
                </b>
                <span>
                  They stay in To document for their owner, and go to HubSpot once completed.
                </span>
              </div>
              <div className="list" aria-label="Not documented yet">
                {todo.map((t) => (
                  <div key={`${t.kind}:${t.id}`} className="todo-row">
                    <button
                      type="button"
                      className="todo-main"
                      onClick={() =>
                        navigate(
                          t.kind === 'lead' ? `/people/leads/${t.id}` : `/people/contacts/${t.id}`,
                        )
                      }
                    >
                      <span className="t1">{t.title}</span>
                      <span className="t2">{t.sub}</span>
                      <span className="le-tags">
                        {t.missing.map((m) => (
                          <span key={m} className="tag warn">
                            {m}
                          </span>
                        ))}
                      </span>
                    </button>
                    {t.ownerId === user.id && (
                      <span className="todo-act">
                        <button
                          type="button"
                          className="btn sm"
                          aria-label={`Complete ${t.title} by keyboard`}
                          onClick={() => navigate(`/todo/${t.kind}/${t.id}`)}
                        >
                          <Icon name="edit" />
                        </button>
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <Notice kind="ok">Everything is documented: it goes, or went, to HubSpot.</Notice>
          )}
        </>
      )}
      {error && <Notice kind="error">{error}</Notice>}
    </Dialog>
  );
}
